import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { max } from 'rxjs';
import { DPFormField } from 'src/app/core/models/DPFormField';
import { DPFormFieldType } from 'src/app/core/models/enums/DPFormFieldType';

/**
 * Field error component.
 *
 * All field validation error messages are aggregated into this component.
 *
 * Refer to DPValidationService.
 */
@Component({
  selector: 'app-field-error',
  standalone: true,
  templateUrl: './field-error.component.html',
  styleUrls: ['./field-error.component.scss'],
})
export class FieldErrorComponent implements OnInit {
  @Input() control: UntypedFormControl;
  @Input() field: DPFormField;

  DPFormFieldType = DPFormFieldType;

  constructor() {}

  ngOnInit(): void {}

  public get errorText(): string {
    if (!this.control?.errors) {
      return '';
    }

    const {
      required,
      email,
      matchField,
      pattern,
      minLength,
      maxLength,
      durationMonthTotal,
      startEndDateTotal,
      invalidateFutureDate,
      invalidatePastDate,
      minStartDate,
      maxEndDate,
      currentResidence,
      currentResidenceSubfield,
      driverLicense,
      dateYearLimit,
      dateOfBirthPlus,
      employerResponseConflicts,
      custom,
    } = this.control.errors;

    if (required) {
      /**
       * boolean
       */
      if (required && this.field.type === DPFormFieldType.YesNo) {
        return `This question is required`;
      } else {
        return `${this.field.label} is required`;
      }
    } else if (email) {
      /**
       * unknown
       */
      return `${this.field.label} is not valid format`;
    } else if (matchField) {
      /**
       * Password match validation error
       *
       * { shouldMatch, otherField }
       *
       * - shouldMatch: Whether the fields should match or not
       * - otherField: Other compared field label
       */
      if (matchField.shouldMatch) {
        return `${this.field.label} does not match to ${matchField.otherField}`;
      } else {
        return `${this.field.label} is same as ${matchField.otherField}`;
      }
    } else if (pattern) {
      /**
       * Pattern validation error.
       *
       * { message: string }
       */
      return pattern.message || `${this.field.label} is not valid format`;
    } else if (minLength) {
      /**
       * Minimum length validation error.
       *
       * { message: string, length: number }
       */
      return (
        minLength.message ||
        `${this.field.label} should be at least ${minLength.length} characters`
      );
    } else if (maxLength) {
      /**
       * Maximum length validation error.
       *
       * { message: string, length: number }
       */
      return (
        maxLength.message ||
        `${this.field.label} should not be more than ${maxLength.value} characters`
      );
    } else if (durationMonthTotal) {
      /**
       * Duration Month Total validation error.
       *
       * { message: string }
       */
      return durationMonthTotal.message || 'Total duration does not match';
    } else if (startEndDateTotal) {
      /**
       * StartEnd Date Total validation error.
       *
       * { message: string, value: number }
       *
       * - message: error message
       * - value: Number of month when history must span from
       */
      if (startEndDateTotal.future) {
        return 'Dates cannot be future dates';
      } else if (startEndDateTotal.value) {
        return (
          startEndDateTotal.message.replace('{val}', startEndDateTotal.value) ||
          `Total history must span ${startEndDateTotal.value} months from now`
        );
      }

      return 'Start/End date total does not match';
    } else if (invalidateFutureDate) {
      /**
       * Invalid future date validation error.
       *
       * { message: string }
       */
      return (
        invalidateFutureDate.message ||
        `${this.field.label} cannot be future date`
      );
    } else if (invalidatePastDate) {
      /**
       * Invalid past date validation error.
       *
       * { message: string }
       */
      return (
        invalidatePastDate.message || `${this.field.label} cannot be past date`
      );
    } else if (minStartDate) {
      /**
       * Minimum start date validation error.
       *
       * { message: string }
       */
      return (
        minStartDate.message ||
        `${this.field.label} cannot be before ${minStartDate.value} years from now`
      );
    } else if (maxEndDate) {
      /**
       * Maximum end date validation error.
       *
       * { message: string }
       */
      return (
        maxEndDate.message ||
        `${this.field.label} cannot be after ${maxEndDate.value} years from now`
      );
    } else if (currentResidence) {
      /**
       * Current residence validation error.
       *
       * { message: string }
       */
      return (
        currentResidence.message ||
        'You cannot have multiple current residence entry'
      );
    } else if (currentResidenceSubfield) {
      /**
       * Current residence subfield validation error.
       *
       * { message: string }
       */
      return (
        currentResidenceSubfield.message ||
        'You cannot have multiple current residence entry'
      );
    } else if (driverLicense) {
      /**
       * Driver licence validation error.
       *
       * { message: string, state: string }
       */
      return (
        driverLicense.message ||
        `${this.field.label} is invalid for ${driverLicense.state}`
      );
    } else if (dateYearLimit) {
      /**
       * Date Year Limit validation error.
       *
       * { message: string }
       */
      return dateYearLimit.message || `${this.field.label} is out of range`;
    } else if (dateOfBirthPlus) {
      /**
       * Date of Birth validation error.
       *
       * { message: string, value: number }
       *
       * - message: error message
       * - value: Number of years specified in rule.
       */
      return (
        dateOfBirthPlus.message ||
        `${this.field.label} should be after you are ${dateOfBirthPlus.value} years old`
      );
    } else if (employerResponseConflicts) {
      return 'Please resolve all conflicts';
    } else if (custom) {
      /**
       * Custom validation message
       *
       * type: string
       */
      return custom;
    }
  }
}
